import type { IconType } from 'react-icons';
import type { Permission } from '../hooks/useValidateSession';
import { ORDER_ACTION_ICONS } from './orderActionIcons';

export const ORDER_SELECT_COLUMN_PX = '40px';

export interface OrderBulkTarget {
    _id: string;
    archived: boolean;
    completed: boolean;
    manuallyFlagged: boolean;
    reviewedByCurrentUser: boolean;
    canComplete: boolean;
}

export type OrderBulkActionId =
    | 'archive'
    | 'unarchive'
    | 'flag'
    | 'unflag'
    | 'mark-reviewed'
    | 'remove-review'
    | 'complete';

export interface OrderBulkActionOption {
    id: OrderBulkActionId;
    label: string;
    icon: IconType;
}

export type OrderBulkListContext = 'home' | 'dashboard-active' | 'dashboard-archived';

export interface OrderBulkPermissions {
    canFlag: boolean;
    canReview: boolean;
    canArchive: boolean;
    canUnarchive: boolean;
    canComplete: boolean;
}

const getMajorityFlagAction = (selected: OrderBulkTarget[]): OrderBulkActionId => {
    const flaggedCount = selected.filter((o) => o.manuallyFlagged).length;
    const unflaggedCount = selected.length - flaggedCount;
    if (flaggedCount > unflaggedCount) return 'unflag';
    return 'flag';
};

const getMajorityReviewAction = (selected: OrderBulkTarget[]): OrderBulkActionId => {
    const reviewedCount = selected.filter((o) => o.reviewedByCurrentUser).length;
    const notReviewedCount = selected.length - reviewedCount;
    if (reviewedCount > notReviewedCount) return 'remove-review';
    return 'mark-reviewed';
};

const BULK_ACTION_SUCCESS_PHRASE: Record<OrderBulkActionId, string> = {
    archive: 'Archived',
    unarchive: 'Unarchived',
    flag: 'Saved For Later',
    unflag: 'Removed from Save For Later',
    'mark-reviewed': 'Marked Reviewed',
    'remove-review': 'Removed from Review',
    complete: 'Marked Complete',
};

export const getBulkActionSuccessTitle = (actionId: OrderBulkActionId, count: number): string => {
    const noun = count === 1 ? 'Order' : 'Orders';
    return `${count} ${noun} ${BULK_ACTION_SUCCESS_PHRASE[actionId]}`;
};

const BULK_ACTION_META: Record<OrderBulkActionId, { label: string; icon: IconType; }> = {
    archive: { label: 'Archive', icon: ORDER_ACTION_ICONS.archive },
    unarchive: { label: 'Unarchive', icon: ORDER_ACTION_ICONS.unarchive },
    flag: { label: 'Save for Later', icon: ORDER_ACTION_ICONS.flag },
    unflag: { label: 'Remove Save for Later', icon: ORDER_ACTION_ICONS.unflag },
    'mark-reviewed': { label: 'Mark Reviewed', icon: ORDER_ACTION_ICONS.markReviewed },
    'remove-review': { label: 'Remove My Review', icon: ORDER_ACTION_ICONS.removeReview },
    complete: { label: 'Mark Complete', icon: ORDER_ACTION_ICONS.complete },
};

export const getOrderBulkPermissions = (
    permissions: Array<Permission>,
): OrderBulkPermissions => {
    return {
        canFlag: permissions.includes('flag-order'),
        canReview: permissions.includes('flag-order'),
        canArchive: permissions.includes('archive-unarchive'),
        canUnarchive: permissions.includes('archive-unarchive'),
        canComplete: permissions.includes('complete-uncomplete'),
    };
};

export const getOrderBulkActions = (
    selected: OrderBulkTarget[],
    permissions: OrderBulkPermissions,
): OrderBulkActionOption[] => {
    if (selected.length === 0) return [];

    const actions: OrderBulkActionOption[] = [];

    const allArchived = selected.every((o) => o.archived);
    const allUnarchived = selected.every((o) => !o.archived);

    if (permissions.canArchive && allUnarchived) {
        actions.push({ id: 'archive', ...BULK_ACTION_META.archive });
    }
    if (permissions.canUnarchive && allArchived) {
        actions.push({ id: 'unarchive', ...BULK_ACTION_META.unarchive });
    }
    if (permissions.canFlag) {
        const flagAction = getMajorityFlagAction(selected);
        actions.push({ id: flagAction, ...BULK_ACTION_META[flagAction] });
    }
    if (permissions.canReview) {
        const reviewAction = getMajorityReviewAction(selected);
        actions.push({ id: reviewAction, ...BULK_ACTION_META[reviewAction] });
    }
    if (
        permissions.canComplete
        && selected.every((o) => o.canComplete && !o.completed)
    ) {
        actions.push({ id: 'complete', ...BULK_ACTION_META.complete });
    }

    return actions;
};
